import React, { useMemo, useContext, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import { utils } from '@yola/ws-sdk';
import DesignOtherStylesContext from '../helpers/design-other-styles-context';

const { SliderPattern } = designSystem;

const isIndeterminate = (values) =>
  values.some((value, index, allValues) => value !== allValues[0]);
const getMaxValue = (values) => [...values].sort((a, b) => parseFloat(b) - parseFloat(a))[0];

function SliderOption({
  id,
  options,
  title,
  popover,
  iconGlyph,
  mixedLabel,
  step,
  min,
  max,
  supportedUnits = [],
  getUnit,
  transformValue,
  computeFallbackValue,
}) {
  const { state, onOptionChange } = useContext(DesignOtherStylesContext);

  const popoverProps = useMemo(
    () => ({
      ...popover,
      content: popover.renderContent(),
    }),
    [popover]
  );

  const properties = useMemo(() => options.map((opt) => opt.property), [options]);

  const initialUnits = useMemo(
    () => properties.map((property) => getUnit(state[property])),
    [properties, getUnit, state]
  );
  const [currentUnits, setCurrentUnits] = useState(initialUnits);

  const values = useMemo(
    () =>
      properties.map((property, index) => {
        if (!state[property]) {
          return computeFallbackValue();
        }

        return currentUnits[index] ? state[property] : parseFloat(state[property]);
      }),
    [properties, state, currentUnits, computeFallbackValue]
  );

  const items = useMemo(
    () =>
      options.map((opt, index) => ({
        value: values[index],
        iconGlyph: opt.iconGlyph,
        onChange: (newValue) => {
          if (!utils.isNumeric(Number(parseFloat(newValue)))) {
            return;
          }

          setCurrentUnits((prev) => prev.map((u, i) => (i === index ? getUnit(newValue) : u)));
          onOptionChange(id, transformValue(newValue), [opt.property]);
        },
      })) || null,
    [options, values, onOptionChange, id, transformValue, getUnit]
  );

  const indeterminate = isIndeterminate(values);
  const value = indeterminate ? getMaxValue(values) : values[0];

  const handleChange = useCallback(
    (newValue) => {
      if (!utils.isNumeric(Number(parseFloat(newValue)))) {
        return;
      }

      setCurrentUnits((prev) => prev.map(() => getUnit(newValue)));
      onOptionChange(id, transformValue(newValue), properties);
    },
    [properties, onOptionChange, id, transformValue, getUnit]
  );

  return (
    <SliderPattern
      value={value}
      title={title}
      items={items}
      iconGlyph={iconGlyph}
      mixedLabel={mixedLabel}
      step={step}
      min={min}
      max={max}
      supportedUnits={supportedUnits}
      indeterminate={indeterminate}
      popoverProps={popoverProps}
      onChange={handleChange}
    />
  );
}

SliderOption.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      iconGlyph: PropTypes.string,
      property: PropTypes.string.isRequired,
    })
  ).isRequired,
  iconGlyph: PropTypes.string.isRequired,
  mixedLabel: PropTypes.string.isRequired,
  popover: PropTypes.object.isRequired,
  step: PropTypes.oneOfType([PropTypes.number, PropTypes.func]).isRequired,
  min: PropTypes.oneOfType([PropTypes.number, PropTypes.func]).isRequired,
  max: PropTypes.oneOfType([PropTypes.number, PropTypes.func]).isRequired,
  supportedUnits: PropTypes.arrayOf(PropTypes.string),
  getUnit: PropTypes.func.isRequired,
  transformValue: PropTypes.func.isRequired,
  computeFallbackValue: PropTypes.func.isRequired,
};

export default SliderOption;
